import { z } from "zod";

import { canonicalJson, type JsonValue } from "./canonical.js";
import {
  calculateResolvedWithHashes,
  type HashedCalculationResult,
} from "./engine.js";
import {
  referenceRulesetSchema,
  resolveReferenceCaseForVerification,
  type ReferenceRuleset,
} from "./reference-ruleset.js";

export const AUTHORITATIVE_RECALCULATION_SCHEMA_VERSION = "1.0.0";
export const AUTHORITATIVE_RECALCULATION_ROUTE =
  "dm140_official_example_verification";

const identifierSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);

export const authoritativeRecalculationRequestSchema = z
  .object({
    schemaVersion: z.literal(AUTHORITATIVE_RECALCULATION_SCHEMA_VERSION),
    requestId: identifierSchema,
    route: z.literal(AUTHORITATIVE_RECALCULATION_ROUTE),
    caseId: identifierSchema,
    expectedInputSha256: sha256Schema,
  })
  .strict();

export type AuthoritativeRecalculationRequest = z.infer<
  typeof authoritativeRecalculationRequestSchema
>;

export interface AuthoritativeRecalculationPayload {
  readonly schemaVersion: typeof AUTHORITATIVE_RECALCULATION_SCHEMA_VERSION;
  readonly requestId: string;
  readonly route: typeof AUTHORITATIVE_RECALCULATION_ROUTE;
  readonly caseId: string;
  readonly status: "recalculated_demo";
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly professionalUseAllowed: false;
  readonly calculation: HashedCalculationResult;
  readonly release: {
    readonly rulesetStatus: "draft";
    readonly calculationEnabled: false;
    readonly blockers: readonly string[];
  };
}

export interface SignedAuthoritativeRecalculation {
  readonly payload: AuthoritativeRecalculationPayload;
  readonly signature: {
    readonly algorithm: "HMAC-SHA256";
    readonly keyId: string;
    readonly value: string;
  };
}

export type AuthoritativeRecalculationErrorCode =
  | "INVALID_REQUEST"
  | "CASE_NOT_SUPPORTED"
  | "INPUT_HASH_MISMATCH"
  | "SIGNING_CONFIGURATION_INVALID";

export class AuthoritativeRecalculationError extends Error {
  readonly code: AuthoritativeRecalculationErrorCode;
  readonly httpStatus: 400 | 409 | 422 | 503;

  constructor(
    code: AuthoritativeRecalculationErrorCode,
    message: string,
    httpStatus: 400 | 409 | 422 | 503,
  ) {
    super(message);
    this.name = "AuthoritativeRecalculationError";
    this.code = code;
    this.httpStatus = httpStatus;
  }
}

function freezeDeep<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach((child) => freezeDeep(child));
  }
  return value;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function hexToBytes(value: string): Uint8Array | null {
  if (!/^[a-f0-9]{64}$/.test(value)) return null;
  const bytes = new Uint8Array(value.length / 2);
  for (let index = 0; index < value.length; index += 2) {
    bytes[index / 2] = Number.parseInt(value.slice(index, index + 2), 16);
  }
  return bytes;
}

async function importSigningKey(secret: string): Promise<CryptoKey> {
  const bytes = new TextEncoder().encode(secret);
  if (bytes.byteLength < 32) {
    throw new AuthoritativeRecalculationError(
      "SIGNING_CONFIGURATION_INVALID",
      "Il segreto di firma server deve contenere almeno 32 byte.",
      503,
    );
  }
  return globalThis.crypto.subtle.importKey(
    "raw",
    bytes,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
}

function parseRequest(input: unknown): AuthoritativeRecalculationRequest {
  const parsed = authoritativeRecalculationRequestSchema.safeParse(input);
  if (!parsed.success) {
    throw new AuthoritativeRecalculationError(
      "INVALID_REQUEST",
      "La richiesta di ricalcolo non rispetta il contratto previsto.",
      400,
    );
  }
  return parsed.data;
}

function parseRuleset(input: unknown): ReferenceRuleset {
  return referenceRulesetSchema.parse(input);
}

export async function createSignedAuthoritativeRecalculation(input: {
  readonly request: unknown;
  readonly ruleset: unknown;
  readonly signingSecret: string;
  readonly keyId: string;
  readonly issuedAt?: Date;
  readonly ttlSeconds?: number;
}): Promise<SignedAuthoritativeRecalculation> {
  const request = parseRequest(input.request);
  const ruleset = parseRuleset(input.ruleset);
  if (request.caseId !== ruleset.reference_case.case_id) {
    throw new AuthoritativeRecalculationError(
      "CASE_NOT_SUPPORTED",
      "Il caso richiesto non è disponibile per il ricalcolo autorevole.",
      422,
    );
  }
  if (!identifierSchema.safeParse(input.keyId).success) {
    throw new AuthoritativeRecalculationError(
      "SIGNING_CONFIGURATION_INVALID",
      "L’identificativo della chiave server non è valido.",
      503,
    );
  }

  const ttlSeconds = input.ttlSeconds ?? 300;
  if (!Number.isInteger(ttlSeconds) || ttlSeconds < 30 || ttlSeconds > 900) {
    throw new AuthoritativeRecalculationError(
      "SIGNING_CONFIGURATION_INVALID",
      "La durata del payload firmato non è valida.",
      503,
    );
  }

  const { calculation } = resolveReferenceCaseForVerification(ruleset);
  const result = await calculateResolvedWithHashes(calculation);
  if (result.inputSha256 !== request.expectedInputSha256) {
    throw new AuthoritativeRecalculationError(
      "INPUT_HASH_MISMATCH",
      "L’impronta degli input non coincide con il caso ricalcolato dal server.",
      409,
    );
  }

  const issuedAt = input.issuedAt ?? new Date();
  if (Number.isNaN(issuedAt.getTime())) {
    throw new AuthoritativeRecalculationError(
      "SIGNING_CONFIGURATION_INVALID",
      "La data server del payload non è valida.",
      503,
    );
  }
  const expiresAt = new Date(issuedAt.getTime() + ttlSeconds * 1_000);
  const payload: AuthoritativeRecalculationPayload = {
    schemaVersion: AUTHORITATIVE_RECALCULATION_SCHEMA_VERSION,
    requestId: request.requestId,
    route: request.route,
    caseId: request.caseId,
    status: "recalculated_demo",
    issuedAt: issuedAt.toISOString(),
    expiresAt: expiresAt.toISOString(),
    professionalUseAllowed: false,
    calculation: result,
    release: {
      rulesetStatus: "draft",
      calculationEnabled: false,
      blockers: [
        "PROFESSIONAL_USE_DISABLED",
        ...ruleset.blocking_issues.map((issue) => issue.code),
      ],
    },
  };
  const key = await importSigningKey(input.signingSecret);
  const message = new TextEncoder().encode(
    canonicalJson(payload as unknown as JsonValue),
  );
  const signature = new Uint8Array(
    await globalThis.crypto.subtle.sign("HMAC", key, message),
  );

  const document: SignedAuthoritativeRecalculation = {
    payload,
    signature: {
      algorithm: "HMAC-SHA256",
      keyId: input.keyId,
      value: bytesToHex(signature),
    },
  };
  return freezeDeep(document);
}

export async function verifySignedAuthoritativeRecalculation(input: {
  readonly document: SignedAuthoritativeRecalculation;
  readonly signingSecret: string;
  readonly at?: Date;
}): Promise<boolean> {
  const signature = hexToBytes(input.document.signature.value);
  if (
    signature === null
    || input.document.signature.algorithm !== "HMAC-SHA256"
  ) {
    return false;
  }
  const at = input.at ?? new Date();
  const issuedAt = Date.parse(input.document.payload.issuedAt);
  const expiresAt = Date.parse(input.document.payload.expiresAt);
  if (
    Number.isNaN(at.getTime())
    || Number.isNaN(issuedAt)
    || Number.isNaN(expiresAt)
    || at.getTime() < issuedAt
    || at.getTime() > expiresAt
  ) {
    return false;
  }
  try {
    const key = await importSigningKey(input.signingSecret);
    const message = new TextEncoder().encode(
      canonicalJson(input.document.payload as unknown as JsonValue),
    );
    const signatureBuffer = new ArrayBuffer(signature.byteLength);
    new Uint8Array(signatureBuffer).set(signature);
    return globalThis.crypto.subtle.verify(
      "HMAC",
      key,
      signatureBuffer,
      message,
    );
  } catch {
    return false;
  }
}
